// Form 11 - Assessment Summary (Read-only)
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  Avatar,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  alpha,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Home as HomeIcon,
  LocationCity as LocationCityIcon,
  DirectionsCar as DirectionsCarIcon,
  Visibility as VisibilityIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";
import DoApiService from "../../../services/DoApiService";
import LaboratoryService from "../../../services/laboratoryService";
import { additionalDocumentService } from "../../../services/additionalDocumentService";
import Form11Service from "../../../services/Form11Service";
import PTProviderService from "../../../services/ptProviderService";
import { getApplicationStage } from "../../../services/api";
import {
  decryptAppId,
  decryptText,
  encryptAppId,
  encryptText,
} from "../../../utils/encryption";
import s3Utils from "../../../utils/s3Utils";

const Form11 = () => {
  // State management
  const [cabDetails, setCabDetails] = useState(null);
  const [TotalNc, setTotalNc] = useState(0);
  const [ncData, setNcData] = useState([]); // Store complete NC array
  const [assessorData, setAssessorData] = useState([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [nonConformities, setNonConformities] = useState({ total: 0 });
  const [assessmentReportData, setAssessmentReportData] = useState(null);
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAfterApprovalBox, setShowAfterApprovalBox] = useState(false);
  const [disabledApprovalBox, setDisabledApprovalBox] = useState(false);
  const [afterApprovalRemark, setAfterApprovalRemark] = useState("");
  const [error, setError] = useState(null);

  // Facility state management
  const [facilityTypes, setFacilityTypes] = useState({
    PermanentFacility: false,
    SiteFacility: false,
    MobileFacility: false,
  });

  // Decision state management
  const [decisionProcess, setDecisionProcess] = useState("");

  // Form inputs state management
  const [formInputs, setFormInputs] = useState({
    internalAudit: "",
    internalAuditType: "", // New field for dropdown selection
    trainedAuditor: "", // Yes/No for cl.8.8.3
    independenceOfActivity: "", // Yes/No for cl.8.8.3
    ncsClosed: "", // Yes/No/NA for cl.8.8.3
    effective: "", // Yes/No for cl.8.8.3
    frequencyOfAudit: "", // Text box for cl.8.8.3
    managementReview: "",
    managementReviewType: "", // New field for management review dropdown
    allPointsCovered: "", // Yes/No for cl.8.9.1
    conductedWithinYear: "", // Yes/No for cl.8.9.1
    ptParticipation: "",
    ptAvailability: "", // Yes/No for PT availability
    ptNotAvailableReason: "", // Text box when PT is not available
    ptParticipationType: "", // Dropdown for assessment team if PT participated
    ptCoverageOfScope: "", // Adequate/Not Adequate
    ptOutlier: "", // Yes/No
    ptOutlierRemarks: "", // Text box for root cause analysis if outlier is Yes
    ilcParticipation: "",
    ilcCoverageOfScope: "", // Adequate/Not Adequate for coverage of scope through PT and/or ILC
    ilcOutlier: "", // Yes/No
    ilcOutlierRemarks: "", // Text box for root cause analysis if outlier is Yes
    otherQualityMeasures: "",
    advisorConsultant: "",
    adequacyOrganization: `Testing & Re-Testing- Adequate
Note - NA/Writing option in note, if something to be mentioned by DO.
Personnel to review the results and authorize the release of reports- Satisfactory.`,
    otherInformation: `No Misuse of the NABL symbol and No complaints against the laboratory.
No unannounced assessment was conducted for the laboratory.
`,
    observationRecommendation: `Recommended grant of accreditation in accordance with ISO 15189:2022 in the field of medical testing for discipline of (Fetch disciplines from application and also give writing option if there is some deletion of discipline/ non recommendation) as per the scope recommendation by the assessment team.
Test at Sl. No (writing option) marked inadequate in form 72 to be included in the scope of accreditation as per Corrective action for the NC Raised (Refer NC No) and comments from the assessor vide email dated: .
Dr. ABC is not accepted to review the results and authorise the release of reports as per the assessment report and request/ informed to the laboratory vide email dated: and acknowledged by laboratory email dated:`,
    acProcessComments: "",
    auditComments: "",
    managementComments: "",
  });

  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Form11 data state
  const [existingForm11Data, setExistingForm11Data] = useState(null);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [loadingForm11Data, setLoadingForm11Data] = useState(false);

  // JD Action states
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionSuccess, setActionSuccess] = useState(null);
  const [actionError, setActionError] = useState(null);

  // PT Data popup state
  const [ptData, setPtData] = useState([]);
  const [ptDataLoading, setPtDataLoading] = useState(false);
  const [ptDataError, setPtDataError] = useState(null);
  const [ptDataPopupOpen, setPtDataPopupOpen] = useState(false);

  // ILC Data popup state
  const [ilcData, setIlcData] = useState([]);
  const [ilcDataLoading, setIlcDataLoading] = useState(false);
  const [ilcDataError, setIlcDataError] = useState(null);
  const [ilcDataPopupOpen, setIlcDataPopupOpen] = useState(false);
  const [applicationStage, setApplicationStage] = useState("");
  // Lightbox state for NC documents
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");

  // Helper function to filter NCs by clause number
  const getNCsByClause = (clauseNumber) => {
    return ncData.filter((nc) => nc.clauseno === clauseNumber);
  };

  // Helper function to parse multiple images separated by ~
  const parseImages = (documentString) => {
    if (!documentString) return [];
    return documentString.split("~").filter((img) => img.trim() !== "");
  };

  // Router hooks
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get encrypted parameters from URL
  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");

  // Decrypt parameters
  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;

  // Helper function to format date range for quality assurance measures
  const formatQualityAssuranceDate = (fromDate, toDate) => {
    const formatSingleDate = (dateString) => {
      if (!dateString) return "Not specified";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      } catch (error) {
        return "Invalid date";
      }
    };

    const from = formatSingleDate(fromDate);
    const to = formatSingleDate(toDate);
    if (from === "Not specified" && to === "Not specified") {
      return "Not specified";
    }
    return `${from} to ${to}`;
  };
  const LoadApplicationStage = useCallback(async () => {
    if (!appId) return;
    try {
      const stageResponse = await getApplicationStage(appId);
      if (stageResponse.data.success) {
        setApplicationStage(stageResponse.data.stage);
      }
    } catch (err) {
      console.error("Load application stage error:", err);
    }
  }, [appId]);
  // Load CAB details
  const loadCABDetails = useCallback(async () => {
    if (!appId) {
      setError("Invalid application ID. Please check the URL.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call API to get CAB details
      const response = await DoApiService.getCABDetails({
        appId: parseFloat(appId),
      });

      if (response.success) {
        setCabDetails(response.data);
        // Bind ConsultancyName to advisorConsultant on first load
        if (response.data.ConsultancyName) {
          setFormInputs((prev) => ({
            ...prev,
            advisorConsultant: response.data.ConsultancyName,
          }));
        }
      } else {
        throw new Error(response.message || "Failed to load CAB details");
      }
    } catch (err) {
      setError(`Error loading CAB details: ${err.message}`);
      console.error("Load CAB details error:", err);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  // Load assessor data
  const loadAssessorData = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await DoApiService.getAssessorAllotmentDetails9({
        mode: 64,
        appId: parseFloat(appId),
      });

      if (response.success) {
        setAssessorData(response.data || []);
      }
    } catch (err) {
      console.error("Load assessor data error:", err);
      setAssessorData([]);
    }
  }, [appId]);

  // Load selected disciplines
  const loadSelectedDisciplines = useCallback(async () => {
    if (!appId || !orgId) return;

    try {
      const result = await LaboratoryService.getSelectedDisciplines(
        appId,
        orgId,
      );

      if (result.success) {
        setSelectedDisciplines(result.data || []);
      } else {
        throw new Error(
          result.message || "Failed to load selected disciplines",
        );
      }
    } catch (err) {
      console.error("Load selected disciplines error:", err);
      // Don't set error state for disciplines as it's not critical for form display
      setSelectedDisciplines([]);
    }
  }, [appId, orgId]);

  // Load Form 215 report data
  const loadNCReportData = useCallback(async () => {
    if (!appId || !cabDetails?.assessmentId) {
      console.log(
        "⚠️ Missing appId or assessmentId, skipping NC report data load",
      );
      return;
    }

    try {
      // Call the actual Form 215 API
      const response = await fetch(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Form215report?assessmentId=${cabDetails.assessmentId}&appId=${appId}`,
      );
      const data = await response.json();

      if (data.isstatus && response.ok) {
        // Set assessment report data from API response
        setAssessmentReportData({
          assessmentSummary: data.assessmentSummary || "N/A",
          assessmentTeamComment: data.assessmentTeamComment || "N/A",
          recommendationOfAssessmentTeam:
            data.recommendationOfAssessmentTeam || "N/A",
          laboratoryName: data.laboratoryName || "N/A",
          assessmentDate: data.assessmentDate || "N/A",
          assessmentType: data.assessmentType || "N/A",
          laName: data.laName || "N/A",
          nonConformities: data.nonConformities || "No",
          nabL133: data.nabL133 || "N/A",
          nabL142: data.nabL142 || "N/A",
          nabL163: data.nabL163 || "N/A",
        });
      } else {
        throw new Error(data.message || "Failed to load Form 215 report data");
      }
    } catch (err) {
      console.error("Load Form 215 report data error:", err);
      // Set fallback values if API fails
      setNonConformities({ total: 0 });
      setAssessmentReportData({
        assessmentSummary: "Assessment summary not available",
        assessmentTeamComment: "Assessment team comments not available",
        recommendationOfAssessmentTeam: "Recommendations not available",
        assessmentType: "N/A",
        assessmentDate: "N/A",
        nonConformities: "No",
        laName: "N/A",
      });
    }
  }, [appId, cabDetails?.assessmentId]);
  const loadTotalNCReportData = useCallback(async () => {
    if (!appId || !cabDetails?.assessmentId) {
      console.log(
        "⚠️ Missing appId or assessmentId, skipping total NC report data load",
      );
      return;
    }

    try {
      // Call the actual Total NC API
      const response = await fetch(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetNCMainall?assessmentId=${cabDetails.assessmentId}&appId=${appId}`,
      );
      const data = await response.json();

      if (data.isstatus && response.ok) {
        setTotalNc(data.nc.length);
        setNcData(data.nc || []); // Store complete NC array
      } else {
        throw new Error(data.message || "Failed to load Total NC report data");
      }
    } catch (err) {
      console.error("Load Total NC report data error:", err);
    }
  }, [appId, cabDetails?.assessmentId]);

  // Load existing Form11 data
  const loadExistingForm11Data = useCallback(async () => {
    if (!appId) {
      console.log("⚠️ Missing appId, skipping Form11 data load");
      return;
    }

    try {
      setLoadingForm11Data(true);

      const response = await Form11Service.getForm11Data(appId);

      if (response.success && response.data) {
        setExistingForm11Data(response.data);
        setHasExistingData(true);
        // ✅ FIX HERE
        if (response.data.afterApproval === 1) {
          setShowAfterApprovalBox(true);
          setAfterApprovalRemark(response.data.afterApprovalRemark);
        } else {
          setShowAfterApprovalBox(false);
          setAfterApprovalRemark("");
        }

        // Update form inputs with existing data
        setFormInputs({
          internalAudit: response.data.InternalAudit || "",
          internalAuditType: response.data.InternalAuditType || "",
          trainedAuditor: response.data.TrainedAuditor || "",
          independenceOfActivity: response.data.IndependenceOfActivity || "",
          ncsClosed: response.data.NcsClosed || "",
          effective: response.data.Effective || "",
          frequencyOfAudit: response.data.FrequencyOfAudit || "",
          managementReview: response.data.ManagementReview || "",
          managementReviewType: response.data.ManagementReviewType || "",
          allPointsCovered: response.data.AllPointsCovered || "",
          conductedWithinYear: response.data.ConductedWithinYear || "",
          ptParticipation: response.data.PtIlcRemark
            ? response.data.PtIlcRemark.split(" | ")
              .find((part) => part.startsWith("PT:"))
              ?.replace("PT: ", "") || ""
            : "",
          ptAvailability: response.data.PtAvailability || "",
          ptNotAvailableReason: response.data.PtNotAvailableReason || "",
          ptParticipationType: response.data.PtParticipationType || "",
          ptCoverageOfScope: response.data.PtCoverageOfScope || "",
          ptOutlier: response.data.PtOutlier || "",
          ptOutlierRemarks: response.data.PtOutlierRemarks || "",
          ilcParticipation: response.data.PtIlcRemark
            ? response.data.PtIlcRemark.split(" | ")
              .find((part) => part.startsWith("ILC:"))
              ?.replace("ILC: ", "") || ""
            : "",
          ilcCoverageOfScope: response.data.IlcCoverageOfScope || "",
          ilcOutlier: response.data.IlcOutlier || "",
          ilcOutlierRemarks: response.data.IlcOutlierRemarks || "",
          otherQualityMeasures: response.data.ProcessWitness || "",
          advisorConsultant: response.data.SecratoryObsv
            ? response.data.SecratoryObsv.split(" | ")
              .find((part) => part.startsWith("Advisor/Consultant:"))
              ?.replace("Advisor/Consultant: ", "") || ""
            : "",
          adequacyOrganization: response.data.SecratoryObsv
            ? response.data.SecratoryObsv.split(" | ")
              .find((part) => part.startsWith("Organization Adequacy:"))
              ?.replace("Organization Adequacy: ", "") || ""
            : "",
          otherInformation: response.data.SecratoryObsv
            ? response.data.SecratoryObsv.split(" | ")
              .find((part) => part.startsWith("Other Information:"))
              ?.replace("Other Information: ", "") || ""
            : "",
          observationRecommendation: response.data.SecratoryObsv
            ? response.data.SecratoryObsv.split(" | ")
              .find((part) =>
                part.startsWith("Observations & Recommendations:"),
              )
              ?.replace("Observations & Recommendations: ", "") || ""
            : "",
          acProcessComments: response.data.ACComment || "",
          auditComments: response.data.AuditComments || "",
          managementComments: response.data.ManagementComments || "",
        });

        // Update facility types if exists
        if (response.data.Facility) {
          const facilityValue = response.data.Facility.toLowerCase();

          setFacilityTypes({
            PermanentFacility: facilityValue.includes("permanent facility"),
            SiteFacility: facilityValue.includes("site facility"),
            MobileFacility: facilityValue.includes("mobile facility"),
          });
        }

        if (
          response.data.DecisionProcess &&
          response.data.DecisionProcess !== null &&
          response.data.DecisionProcess !== ""
        ) {
          let decisionValue;
          // Handle different possible values from API
          const apiDecision = response.data.DecisionProcess.toString()
            .toLowerCase()
            .trim();

          if (
            apiDecision === "internal" ||
            apiDecision === "internal_approval" ||
            apiDecision === "through internal approval"
          ) {
            decisionValue = "internal_approval";
          } else if (
            apiDecision === "acm" ||
            apiDecision === "external" ||
            apiDecision === "through acm"
          ) {
            decisionValue = "acm";
          } else {
            decisionValue = response.data.DecisionProcess; // Use as-is if doesn't match expected values
          }
          setDecisionProcess(decisionValue);
        } else {
          setDecisionProcess(""); // Reset to empty if no value
        }
      } else {
        setExistingForm11Data(null);
        setHasExistingData(false);
      }
    } catch (err) {
      console.error("Load existing Form11 data error:", err);
      setExistingForm11Data(null);
      setHasExistingData(false);
    } finally {
      setLoadingForm11Data(false);
    }
  }, [appId]);

  // Load additional documents
  const loadAdditionalDocuments = useCallback(async () => {
    if (!appId || !orgId || !cabDetails?.assessmentId) {
      console.log(
        "⚠️ Missing required parameters for additional documents, skipping load",
      );
      return;
    }

    try {
      setLoadingDocuments(true);
      const response = await additionalDocumentService.getDocuments({
        orgId: orgId,
        appId: appId,
        targetType: "assessor",
        assessmentId: cabDetails.assessmentId,
      });

      if (response.success) {
        setAdditionalDocuments(response.data || []);
      } else {
        setAdditionalDocuments([]);
      }
    } catch (err) {
      console.error("Load additional documents error:", err);
      setAdditionalDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, [appId, orgId, cabDetails?.assessmentId]);

  // Load data when component mounts
  useEffect(() => {
    loadCABDetails();
    loadAssessorData();
    loadSelectedDisciplines();
    loadExistingForm11Data();
    LoadApplicationStage();
  }, [
    loadCABDetails,
    loadAssessorData,
    loadSelectedDisciplines,
    loadExistingForm11Data,
    LoadApplicationStage,
  ]);

  // Debug: Watch for decisionProcess state changes
  useEffect(() => {
    console.log("🎯 Decision Process state changed to:", decisionProcess);
  }, [decisionProcess]);

  // Load NC report data and additional documents after CAB details are loaded
  useEffect(() => {
    if (cabDetails?.assessmentId) {
      loadNCReportData();
      loadTotalNCReportData();
      loadAdditionalDocuments();
    }
  }, [
    cabDetails?.assessmentId,
    loadNCReportData,
    loadTotalNCReportData,
    loadAdditionalDocuments,
  ]);

  // Update facility types when cabDetails change
  useEffect(() => {
    if (cabDetails) {
      setFacilityTypes({
        PermanentFacility:
          cabDetails.PermanentFacility === true ||
          cabDetails.PermanentFacility === "true",
        SiteFacility:
          cabDetails.SiteFacility === true ||
          cabDetails.SiteFacility === "true",
        MobileFacility:
          cabDetails.MobileFacility === true ||
          cabDetails.MobileFacility === "true",
      });
    }
  }, [cabDetails]);

  // Handle facility type change
  const handleFacilityChange = (facilityType) => (event) => {
    const newValue = event.target.checked;
    setFacilityTypes((prev) => ({
      ...prev,
      [facilityType]: newValue,
    }));

    // You can add API call here to save the changes
    // await DoApiService.updateFacilityTypes(appId, { ...facilityTypes, [facilityType]: newValue });
  };

  // Handle form input changes
  const handleInputChange = (fieldName) => (event) => {
    const value = event.target.value;
    setFormInputs((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Handle form submission
  const handleSubmitForm = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      // Extract form data from current component state
      const formData = Form11Service.extractFormDataFromState({
        cabDetails,
        selectedDisciplines,
        assessorData,
        facilityTypes,
        assessmentReportData,
        formInputs: {
          ...formInputs,
          decisionProcess: decisionProcess,
          totalNc: TotalNc || 0,
        },
      });

      // Add additional required fields
      const preparedData = Form11Service.prepareFormDataForSubmission(
        formData,
        {
          appId: parseInt(appId),
          orgId: localStorage.getItem("orgId"),
          insertedBy: "JD_User", // You can get this from user context
          insertIp: window.location.remoteAddress,
        },
      );

      // Validate the data
      const validation = Form11Service.validateFormData(preparedData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(", "));
      }

      // Submit to API
      const result = await Form11Service.insertForm11Data(preparedData);

      if (result.success) {
        setSubmitSuccess(true);
        setTimeout(() => {
          navigate(-1);
        }, 3000);

        // Show success message for 3 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 3000);
      } else {
        throw new Error(result.error || "Failed to submit Form11 data");
      }
    } catch (error) {
      console.error("❌ Error submitting Form11:", error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load PT Provider data for popup (only PT data)
  const loadPTData = useCallback(async () => {
    try {
      setPtDataLoading(true);
      setPtDataError(null);
      console.log("Loading PT data with appId:", appId, "orgId:", orgId);

      const response = await PTProviderService.getPTProviderData(appId, orgId);

      if (response.success) {
        // Filter only PT data (includes PT and PT/EQA types)
        const filteredPTData = (response.data || []).filter(
          (item) =>
            item.participationIn === "PT" ||
            item.participationIn === "PT/EQA (NABL Accredited)" ||
            item.participationIn === "PT/EQA (Others)" ||
            item.participationIn?.toLowerCase().includes("pt"),
        );
        setPtData(filteredPTData);
        console.log(
          "PT data loaded successfully:",
          filteredPTData.length,
          "PT records",
        );
      } else {
        setPtData([]);
        console.warn("PT data API returned success: false");
      }
    } catch (error) {
      console.error("Error loading PT provider data:", error);
      setPtDataError("Failed to load PT data. Please try again.");
      setPtData([]);
    } finally {
      setPtDataLoading(false);
    }
  }, [appId, orgId]);

  // Load ILC data for popup (only ILC data)
  const loadILCData = useCallback(async () => {
    try {
      setIlcDataLoading(true);
      setIlcDataError(null);
      console.log("Loading ILC data with appId:", appId, "orgId:", orgId);

      const response = await PTProviderService.getPTProviderData(appId, orgId);

      if (response.success) {
        // Filter only ILC data
        const filteredILCData = (response.data || []).filter(
          (item) => item.participationIn === "ILC",
        );
        setIlcData(filteredILCData);
        console.log(
          "ILC data loaded successfully:",
          filteredILCData.length,
          "ILC records",
        );
      } else {
        setIlcData([]);
        console.warn("ILC data API returned success: false");
      }
    } catch (error) {
      console.error("Error loading ILC data:", error);
      setIlcDataError("Failed to load ILC data. Please try again.");
      setIlcData([]);
    } finally {
      setIlcDataLoading(false);
    }
  }, [appId, orgId]);

  // Handle PT data popup
  const handleViewPTData = async () => {
    setPtDataPopupOpen(true);
    await loadPTData();
  };

  const handleClosePTDataPopup = () => {
    setPtDataPopupOpen(false);
    setPtDataError(null);
  };

  // Handle ILC data popup
  const handleViewILCData = async () => {
    setIlcDataPopupOpen(true);
    await loadILCData();
  };

  const handleCloseILCDataPopup = () => {
    setIlcDataPopupOpen(false);
    setIlcDataError(null);
  };

  // Helper function to get provider name based on participation type
  const getProviderName = (
    participationIn,
    nodalLaboratory,
    nodalLaboratoryOthers,
  ) => {
    if (participationIn === "PT" && nodalLaboratory) {
      return nodalLaboratory;
    }
    if (participationIn === "PT/EQA (Others)" && nodalLaboratoryOthers) {
      return nodalLaboratoryOthers;
    }
    return "N/A";
  };

  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1);
  };

  // Handle JD Approval - Show confirmation dialog
  const handleApprove = () => {
    setApprovalDialogOpen(true);
  };

  // Handle approval confirmation
  const handleApproveConfirm = async () => {
    try {
      setIsApproving(true);
      setActionError(null);
      setApprovalDialogOpen(false);

      const approvalData = {
        appId: parseInt(appId),
        orgId: parseInt(orgId),
        jDirectorAccepted: 1, // Send as integer, not string
        jDirectorAcceptDate: new Date().toISOString(),
        jDirectorIP: window.location.hostname || "localhost",
        jDirectorDenyReason: null,
      };

      // Temporary direct API call to bypass caching issue
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5000/reactApi/api";
      const token = localStorage.getItem("authToken");
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(`${API_BASE_URL}/form11/jd-decision`, {
        method: "PUT",
        headers: headers,
        body: JSON.stringify(approvalData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const result = await response.json();

      if (result.success) {
        setActionSuccess(
          "✅ Application approved successfully! and Send to Director For Approval",
        );

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setActionSuccess(null);
        }, 5000);
        setTimeout(() => {
          navigate(-1);
        }, 3000);

        // Reload page data
        loadExistingForm11Data();
      } else {
        throw new Error(result.error || "Failed to approve application");
      }
    } catch (error) {
      console.error("❌ Error approving Form11:", error);
      setActionError(error.message);
    } finally {
      setIsApproving(false);
    }
  };

  // Handle approval dialog cancel
  const handleApproveCancel = () => {
    setApprovalDialogOpen(false);
  };

  // Handle JD Rejection
  const handleReject = () => {
    setRejectionDialogOpen(true);
    setRejectionReason("");
  };

  // Handle rejection confirmation
  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      setActionError("Please provide a reason for rejection");
      return;
    }

    try {
      setIsRejecting(true);
      setActionError(null);

      const rejectionData = {
        appId: parseInt(appId),
        orgId: parseInt(orgId),
        jDirectorAccepted: 2, // Send as integer, not string
        jDirectorAcceptDate: new Date().toISOString(),
        jDirectorIP: window.location.hostname || "localhost",
        jDirectorDenyReason: rejectionReason.trim(),
      };

      // Temporary direct API call to bypass caching issue
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5000/reactApi/api";
      const token = localStorage.getItem("authToken");
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(`${API_BASE_URL}/form11/jd-decision`, {
        method: "PUT",
        headers: headers,
        body: JSON.stringify(rejectionData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const result = await response.json();

      if (result.success) {
        setActionSuccess("✅ Application rejected successfully!");
        setRejectionDialogOpen(false);

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setActionSuccess(null);
        }, 5000);
        setTimeout(() => {
          navigate(-1);
        }, 3000);

        // Reload page data
        loadExistingForm11Data();
      } else {
        throw new Error(result.error || "Failed to reject application");
      }
    } catch (error) {
      console.error("❌ Error rejecting Form11:", error);
      setActionError(error.message);
    } finally {
      setIsRejecting(false);
    }
  };

  // Handle rejection dialog close
  const handleRejectCancel = () => {
    setRejectionDialogOpen(false);
    setRejectionReason("");
  };

  // Handle NC report view navigation
  const handleViewNCReport = () => {
    // Create encrypted parameters for the NC report URL
    const encryptedAppIdParam = encryptedAppId; // Use the existing encrypted appId
    const ncReportUrl = `/main/jd/nc-report?appId=${encryptedAppIdParam}&assessmentId=${encryptText(cabDetails?.assessmentId)}`;
    // Open in new tab
    window.open(ncReportUrl, "_blank", "noopener,noreferrer");
  };
  const handleViewwitnessReport = () => {
    // Create encrypted parameters for the witness report URL
    const encryptedAppIdParam = encryptedAppId; // Use the existing encrypted appId
    const witnessReportUrl = `/main/jd/FormTestWitness?appId=${encryptedAppIdParam}&assessmentId=${encryptText(cabDetails?.assessmentId)}`;
    // Open in new tab
    window.open(witnessReportUrl, "_blank", "noopener,noreferrer");
  };
  const handleViewScopeReport = () => {
    // Create encrypted parameters for the scope report URL
    const scopeReportUrl = `/main/jd/form72?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(cabDetails?.assessmentId)}`;
    // Open in new tab
    window.open(scopeReportUrl, "_blank", "noopener,noreferrer");
  };
  const handleView73Report = () => {
    // Create encrypted parameters for the scope report URL
    const scopeReportUrl = `/main/jd/form73?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&observation=${encryptText("true")}&assessmentId=${encryptText(cabDetails?.assessmentId)}`;
    // Open in new tab
    window.open(scopeReportUrl, "_blank", "noopener,noreferrer");
  };

  // Handle view profile action
  const handleViewProfile = (assessor) => {
    const profileUrl = `/main/assessor-profile?AssessorId=${encryptText(assessor.allotedToId)}`;
    window.open(profileUrl, "_blank", "noopener,noreferrer");
  };

  // Handle viewing additional documents
  const handleViewDocument = async (document) => {
    if (document.fileKey || document.fileName) {
      const success = await s3Utils.handleFileViewWithS3(
        document.fileKey || document.fileName,
        {
          showError: (msg) => setError(msg),
          showLoading: () => setLoading(true),
          hideLoading: () => setLoading(false),
        },
      );

      if (!success) {
        setError("Failed to open document");
      }
    }
  };

  const handleViewNAF6 = () => {
      const naf6Url = `/main/view-NAF6?appId=${encryptAppId(appId)}&assessmentId=${encryptText(cabDetails?.assessmentId)}`;
      window.open(naf6Url, "_blank", "noopener,noreferrer");
    }

  // Get target type display name
  const getTargetTypeDisplay = (type) => {
    switch (type) {
      case "lab":
        return "Laboratory";
      case "assessor":
        return "Assessor";
      case "do":
        return "NABL";
      default:
        return type;
    }
  };

  // Get target type color
  const getTargetTypeColor = (type) => {
    switch (type) {
      case "lab":
        return "primary";
      case "assessor":
        return "secondary";
      case "do":
        return "info";
      default:
        return "default";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Mock disciplines data (you can replace this with actual API data)
  const existingDisciplines =
    selectedDisciplines.length > 0
      ? selectedDisciplines.map(
        (discipline) =>
          discipline.disciplineName ||
          discipline.name ||
          discipline.title ||
          "Unknown Discipline",
      )
      : [
        "Clinical Biochemistry",
        "Clinical Pathology",
        "Histopathology",
        "Cytopathology",
        "Haematology",
        "Microbiology & Infectious disease serology",
        "Molecular Testing",
      ];

  // Get capacity color for chip display
  const getCapacityColor = (capacity) => {
    const colors = {
      "Lead Assessor": "primary",
      "Technical Assessor": "success",
      Observer: "secondary",
      "Technical Expert": "warning",
      "Team Leader": "error",
    };
    return colors[capacity] || "default";
  };

  // Colors object for document components
  const colors = {
    primary: { main: "#1976d2" },
    grey: { 100: "#f5f5f5", 300: "#e0e0e0", 700: "#616161" },
    error: { main: "#d32f2f" },
    success: { main: "#2e7d32" },
  };

  // Handle lightbox for images
  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  // Single Document Component
  const SingleDocument = ({
    doc,
    index,
    isPdf,
    isExcel,
    colors,
    onImageClick,
  }) => {
    const [imageUrl, setImageUrl] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadDocument = async () => {
        try {
          setLoading(true);
          if (doc) {
            // Check if it's just a filename or full URL
            if (s3Utils.isJustFilename(doc)) {
              const signedUrl = await s3Utils.generateSignedUrlForFilename(doc);
              setImageUrl(signedUrl);
            } else {
              setImageUrl(doc);
            }
          }
        } catch (error) {
          console.error("Error loading document:", error);
          setImageUrl("");
        } finally {
          setLoading(false);
        }
      };

      loadDocument();
    }, [doc]);

    if (loading) {
      return (
        <Box
          sx={{
            width: 80,
            height: 80,
            border: `2px solid ${colors.grey[300]}`,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: colors.grey[100],
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      );
    }

    // For PDF/Excel files, show thumbnail with icon
    if (isPdf || isExcel) {
      return (
        <Box
          sx={{
            width: 80,
            height: 80,
            border: `2px solid ${colors.grey[300]}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s",
            bgcolor: isPdf
              ? alpha(colors.error.main, 0.05)
              : alpha(colors.success.main, 0.05),
            "&:hover": {
              borderColor: isPdf ? colors.error.main : colors.success.main,
              transform: "scale(1.05)",
              boxShadow: 3,
            },
          }}
          onClick={() => window.open(imageUrl, "_blank")}
        >
          {isPdf ? (
            <PictureAsPdfIcon
              sx={{ fontSize: 30, color: colors.error.main, mb: 1 }}
            />
          ) : (
            <DescriptionIcon
              sx={{ fontSize: 30, color: colors.success.main, mb: 1 }}
            />
          )}
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, textAlign: "center", px: 1 }}
          >
            {isPdf ? "PDF" : "Excel"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.40rem" }}
          >
            Click to view
          </Typography>
        </Box>
      );
    }

    // For images, show the actual image
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            borderColor: colors.primary.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() =>
          onImageClick
            ? onImageClick(imageUrl)
            : window.open(imageUrl, "_blank")
        }
      >
        <img
          src={imageUrl}
          alt={`Evidence ${index + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.src =
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
          }}
        />
      </Box>
    );
  };

  // Working Space Document Component
  const WorkingSpaceDocument = ({
    doc,
    index,
    isImage,
    isPdf,
    isExcel,
    colors,
    onImageClick,
  }) => {
    // Check if doc contains multiple files separated by ~
    if (doc && doc.includes("~")) {
      const files = doc.split("~");
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {files.map((file, fileIndex) => {
            const cleanFile = file.trim();
            if (!cleanFile) return null;

            const fileIsPdf = cleanFile.toLowerCase().endsWith(".pdf");
            const fileIsExcel = cleanFile.toLowerCase().match(/\.(xls|xlsx)$/);

            return (
              <Box
                key={fileIndex}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: colors.primary.main,
                    minWidth: 50,
                  }}
                >
                  Doc {fileIndex + 1}:
                </Typography>
                <SingleDocument
                  doc={cleanFile}
                  index={fileIndex}
                  isPdf={fileIsPdf}
                  isExcel={fileIsExcel}
                  colors={colors}
                  onImageClick={onImageClick}
                />
              </Box>
            );
          })}
        </Box>
      );
    }

    // Single file - use the original logic
    return (
      <SingleDocument
        doc={doc}
        index={index}
        isPdf={isPdf}
        isExcel={isExcel}
        colors={colors}
        onImageClick={onImageClick}
      />
    );
  };

  // Handle QMS Document viewing
  const handleViewQMSDocument = async () => {
    if (!cabDetails?.QMSFile) {
      setError("QMS Document not found");
      return;
    }

    try {
      setLoading(true);
      const success = await s3Utils.handleFileViewWithS3(cabDetails.QMSFile, {
        showError: (msg) => setError(msg),
        showLoading: () => setLoading(true),
        hideLoading: () => setLoading(false),
      });

      if (!success) {
        setError("Failed to open QMS Document");
      }
    } catch (error) {
      console.error("Error opening QMS Document:", error);
      setError("Failed to open QMS Document");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Form 11...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={handleBackClick}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        pt: 2,
        pb: 4,
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
          color: "white",
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            sx={{
              mb: 2,
              "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.7)" },
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={handleBackClick}
              sx={{
                textDecoration: "none",
                color: "rgba(255,255,255,0.8)",
                "&:hover": { color: "white" },
              }}
            >
              Dashboard
            </Link>
            <Typography variant="body2" sx={{ color: "white" }}>
              Form 11 - Assessment Summary
            </Typography>
          </Breadcrumbs>

          {/* Header Content */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  mr: 2,
                  width: 56,
                  height: 56,
                }}
              >
                <DescriptionIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Assessment Summary - Form 11
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  {cabDetails?.applicationNo || "N/A"} •{" "}
                  {cabDetails?.labType || "N/A"}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ px: 3 }}>
        {/* CAB Information Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{ bgcolor: "primary.main", mr: 2, width: 40, height: 40 }}
                >
                  <BusinessIcon />
                </Avatar>

                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  CAB Information
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleViewNAF6}
              >
                View NAF6
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    CAB Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cabDetails?.labName || cabDetails?.laboratoryName || "N/A"}
                    , {cabDetails?.cityName || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    CAB ID.
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cabDetails?.applicationNo || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Field
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cabDetails?.labType || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Type of Assessment
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cabDetails?.applicationType || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Date of receipt of application
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(cabDetails?.submissionDate) || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Standard
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    ISO 15189:{cabDetails?.standard || "2022"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Premises assessed
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cabDetails?.AddressLine1 ||
                      cabDetails?.AddressLine2 ||
                      "N/A"}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    State:{" "}
                    {cabDetails?.stateName || cabDetails?.stateName || "N/A"}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    City:{" "}
                    {cabDetails?.cityName || cabDetails?.cityName || "N/A"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Validity / Current status of accreditation
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {existingForm11Data?.CurrentStatus ||
                      existingForm11Data?.currentStatus ||
                      "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Extension of validity (if applicable)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {existingForm11Data?.ExtnValidity || "Not applicable"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Disciplines Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{ bgcolor: "success.main", mr: 2, width: 40, height: 40 }}
              >
                <AssignmentIcon />
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Discipline(s)
              </Typography>
            </Box>

            {/* Existing Disciplines */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              (a) Existing{" "}
              {selectedDisciplines.length > 0 &&
                `(${selectedDisciplines.length} found)`}
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>S No.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Discipline</TableCell>
                    {selectedDisciplines.length > 0 &&
                      selectedDisciplines[0]?.groupName && (
                        <TableCell sx={{ fontWeight: 600 }}>Group</TableCell>
                      )}
                    {selectedDisciplines.length > 0 &&
                      selectedDisciplines[0]?.facilityName && (
                        <TableCell sx={{ fontWeight: 600 }}>Facility</TableCell>
                      )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedDisciplines.length > 0 ? (
                    selectedDisciplines.map((discipline, index) => (
                      <TableRow key={discipline.id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {discipline.DisciplineName ||
                            discipline.name ||
                            discipline.title ||
                            "N/A"}
                        </TableCell>
                        {discipline.groupName && (
                          <TableCell>{discipline.groupName}</TableCell>
                        )}
                        {discipline.facilityName && (
                          <TableCell>{discipline.facilityName}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : existingDisciplines.length > 0 ? (
                    existingDisciplines.map((discipline, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{discipline}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{
                          textAlign: "center",
                          fontStyle: "italic",
                          color: "text.secondary",
                        }}
                      >
                        No disciplines found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Scope Extension */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              (b) Scope Extension, (if any)
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>S No.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Group</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Discipline</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Facility</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{
                        textAlign: "center",
                        fontStyle: "italic",
                        color: "text.secondary",
                      }}
                    >
                      No scope extension requested
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Facility Section - Show only those that are true/1 */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Facility Types
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                {/* Permanent Facility - Show only if true */}
                {facilityTypes.PermanentFacility && (
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "primary.main",
                        borderRadius: 2,
                        backgroundColor: "primary.50",
                        opacity: 0.8,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={true}
                            disabled={true}
                            color="primary"
                            icon={<HomeIcon />}
                            checkedIcon={<HomeIcon />}
                          />
                        }
                        label={
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            Permanent Facility
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                )}

                {/* Site Facility - Show only if true */}
                {facilityTypes.SiteFacility && (
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "secondary.main",
                        borderRadius: 2,
                        backgroundColor: "secondary.50",
                        opacity: 0.8,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={true}
                            disabled={true}
                            color="secondary"
                            icon={<LocationCityIcon />}
                            checkedIcon={<LocationCityIcon />}
                          />
                        }
                        label={
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            Site Facility
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                )}

                {/* Mobile Facility - Show only if true */}
                {facilityTypes.MobileFacility && (
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "success.main",
                        borderRadius: 2,
                        backgroundColor: "success.50",
                        opacity: 0.8,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={true}
                            disabled={true}
                            color="success"
                            icon={<DirectionsCarIcon />}
                            checkedIcon={<DirectionsCarIcon />}
                          />
                        }
                        label={
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            Mobile Facility
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                )}

                {/* Show message if no facilities are available */}
                {!facilityTypes.PermanentFacility &&
                  !facilityTypes.SiteFacility &&
                  !facilityTypes.MobileFacility && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 3,
                          border: 1,
                          borderColor: "grey.300",
                          borderRadius: 2,
                          backgroundColor: "grey.50",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", fontStyle: "italic" }}
                        >
                          No facility types found in the application data
                        </Typography>
                      </Box>
                    </Grid>
                  )}
              </Grid>
            </Box>
          </CardContent>
        </Card>

        {/* Assessment Team Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{ bgcolor: "info.main", mr: 2, width: 40, height: 40 }}
              >
                <PeopleIcon />
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Assessment Team
              </Typography>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>S. No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Laboratory Details
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Assessor Details
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expertise</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Organisation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Assessment Dates
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assessorData && assessorData.length > 0 ? (
                    assessorData.map((assessor, index) => (
                      <TableRow key={assessor.id || index}>
                        <TableCell>
                          <Box sx={{ textAlign: "center" }}>
                            {index + 1} <br />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewProfile(assessor)}
                              sx={{ ml: 1 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "small" }}
                          >
                            {assessor.laboratoryName || "N/A"}
                          </Typography>
                          <Chip
                            label={assessor.applicationNo || "N/A"}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium", mb: 0.5 }}
                          >
                            {assessor.assessorName || "N/A"}
                          </Typography>
                          <Chip
                            sx={{ mb: 0.5 }}
                            label={
                              assessor.capacity ||
                              assessor.assessorCapacity ||
                              "Technical Assessor"
                            }
                            size="small"
                            color={getCapacityColor(
                              assessor.capacity || assessor.assessorCapacity,
                            )}
                            variant="outlined"
                          />
                          <Chip
                            label={assessor.loginName || "N/A"}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {assessor.fieldOfExpertise ||
                              assessor.expertise ||
                              "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {assessor.organisation ||
                              assessor.organization ||
                              "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "success.main",
                                fontSize: "0.75rem",
                              }}
                            >
                              From:{" "}
                              {formatDate(
                                assessor.fromDate || assessor.startDate,
                              )}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "error.main", fontSize: "0.75rem" }}
                            >
                              To:{" "}
                              {formatDate(assessor.toDate || assessor.endDate)}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Typography variant="body1" color="text.secondary">
                          No assessment team data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Assessment Results Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{ bgcolor: "warning.main", mr: 2, width: 40, height: 40 }}
              >
                <AssessmentIcon />
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Observations/ Comments of the Assessment team
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Observations/Comments */}

              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: "#fff3e0", borderRadius: 2, mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Test/ Calibration/ PT Scheme /RMP process witnessed
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={handleViewwitnessReport}
                    sx={{
                      borderColor: "warning.main",
                      color: "warning.main",
                      "&:hover": {
                        borderColor: "warning.dark",
                        backgroundColor: "warning.50",
                      },
                    }}
                  >
                    View Details
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: "#e8f5e8", borderRadius: 2, mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Present status of Non Conformities
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={handleViewNCReport}
                    sx={{
                      borderColor: "success.main",
                      color: "success.main",
                      "&:hover": {
                        borderColor: "success.dark",
                        backgroundColor: "success.50",
                      },
                    }}
                  >
                    View Details
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{ p: 2, bgcolor: "#ebfffdff", borderRadius: 2, mb: 2 }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Recommended Scope (Form72):
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={handleViewScopeReport}
                    sx={{
                      borderColor: "primary.main",
                      color: "primary.main",
                      "&:hover": {
                        borderColor: "primary.dark",
                        backgroundColor: "primary.50",
                      },
                    }}
                  >
                    View Details
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{ p: 2, bgcolor: "#ebf1ffff", borderRadius: 2, mb: 2 }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Person declared to report, review and authorized the result:
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={handleView73Report}
                    sx={{
                      borderColor: "primary.main",
                      color: "primary.main",
                      "&:hover": {
                        borderColor: "primary.dark",
                        backgroundColor: "primary.50",
                      },
                    }}
                  >
                    View Details
                  </Button>
                </Box>
              </Grid>
              {/* Non-Conformities */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: "#ffebee", borderRadius: 2, mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Total No. of Non-Conformities: {TotalNc || 0}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Quality Assurance Measures Section */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Quality Assurance Measures
            </Typography>
            <Grid container spacing={3}>
              {/* Internal Audit */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Internal Audit
                  </Typography>
                  {cabDetails?.InternalAuditFromDate ||
                  cabDetails?.InternalAuditToDate ? (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#1976d2", mb: 0.5 }}
                      >
                        📅 Internal Audit Dates:
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#333" }}>
                        {formatQualityAssuranceDate(
                          cabDetails.InternalAuditFromDate,
                          cabDetails.InternalAuditToDate,
                        )}
                      </Typography>
                    </Box>
                  ) : null}

                  {/* cl.8.8.3 Assessment Questions - Always Visible */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 3,
                      backgroundColor: "#f8f9ff",
                      borderRadius: 2,
                      border: "1px solid #e3f2fd",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 2, color: "#1976d2" }}
                    >
                      Clause 8.8.3 Assessment Questions
                    </Typography>
                    <Grid container spacing={3}>
                      {/* Trained Auditor */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Trained Auditor:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Chip
                            label={formInputs.trainedAuditor || "Not specified"}
                            color={
                              formInputs.trainedAuditor === "Yes"
                                ? "success"
                                : formInputs.trainedAuditor === "No"
                                  ? "error"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Grid>

                      {/* Independence of Activity */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Independence of Activity being audited:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Chip
                            label={
                              formInputs.independenceOfActivity ||
                              "Not specified"
                            }
                            color={
                              formInputs.independenceOfActivity === "Yes"
                                ? "success"
                                : formInputs.independenceOfActivity === "No"
                                  ? "error"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Grid>

                      {/* NCs Closed */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Status of the Non-Conformity raised during the
                          internal audit is closed or not? :
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Chip
                            label={formInputs.ncsClosed || "Not specified"}
                            color={
                              formInputs.ncsClosed === "Yes"
                                ? "success"
                                : formInputs.ncsClosed === "No"
                                  ? "error"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Grid>

                      {/* Effective */}
                      {/* <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Effective:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Chip
                            label={formInputs.effective || "Not specified"}
                            color={
                              formInputs.effective === "Yes"
                                ? "success"
                                : formInputs.effective === "No"
                                  ? "error"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Grid> */}

                      {/* Frequency of Internal Audit */}
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Frequency of Internal Audit:
                        </Typography>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: "#f8f9fa",
                            borderRadius: 1,
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {formInputs.frequencyOfAudit ||
                              "No information provided"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Display NCs for Clause 8.8.3 */}
                    {getNCsByClause("8.8.3").length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 2, color: "#1976d2" }}
                        >
                          Non-Conformities (Clause 8.8.3)
                        </Typography>
                        {getNCsByClause("8.8.3").map((nc, index) => (
                          <Box
                            key={nc.id}
                            sx={{
                              mb: 2,
                              p: 2,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Description:</strong> {nc.description}
                            </Typography>
                            {nc.document &&
                              nc.document.split("~").filter(Boolean).length >
                                0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      color: colors.grey[700],
                                      mb: 1,
                                      display: "block",
                                    }}
                                  >
                                    Documents:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                    }}
                                  >
                                    {nc.document
                                      .split("~")
                                      .filter(Boolean)
                                      .map((doc, i) => {
                                        const lower = doc.toLowerCase();
                                        return (
                                          <WorkingSpaceDocument
                                            key={i}
                                            doc={doc}
                                            index={i}
                                            isImage={lower.match(
                                              /\.(jpg|jpeg|png|gif|webp)$/,
                                            )}
                                            isPdf={lower.endsWith(".pdf")}
                                            isExcel={lower.match(
                                              /\.(xls|xlsx)$/,
                                            )}
                                            colors={colors}
                                            onImageClick={handleOpenLightbox}
                                          />
                                        );
                                      })}
                                  </Box>
                                </Box>
                              )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, mt: 2, fontWeight: 500 }}
                      >
                        Comments:
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#f8f9fa",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {formInputs.auditComments ||
                            "No information provided"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* Management Review */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Management Review
                  </Typography>
                  {cabDetails?.ManagementReviewFromDate ||
                  cabDetails?.ManagementReviewToDate ? (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#1976d2", mb: 0.5 }}
                      >
                        📅 Management Review Dates:
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#333" }}>
                        {formatQualityAssuranceDate(
                          cabDetails.ManagementReviewFromDate,
                          cabDetails.ManagementReviewToDate,
                        )}
                      </Typography>
                    </Box>
                  ) : null}

                  {/* cl.8.9.1 Assessment Questions - Always Visible */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 3,
                      backgroundColor: "#f0f8f0",
                      borderRadius: 2,
                      border: "1px solid #e8f5e8",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 2, color: "#2e7d32" }}
                    >
                      Clause 8.9 Assessment Questions
                    </Typography>
                    <Grid container spacing={3}>
                      {/* All points of Standard covered */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          All points of Standard covered:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Chip
                            label={
                              formInputs.allPointsCovered || "Not specified"
                            }
                            color={
                              formInputs.allPointsCovered === "Yes"
                                ? "success"
                                : formInputs.allPointsCovered === "No"
                                  ? "error"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Grid>

                      {/* Conducted within a year */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Conducted within a year:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Chip
                            label={
                              formInputs.conductedWithinYear || "Not specified"
                            }
                            color={
                              formInputs.conductedWithinYear === "Yes"
                                ? "success"
                                : formInputs.conductedWithinYear === "No"
                                  ? "error"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Display NCs for Clause 8.9 */}
                    {getNCsByClause("8.9").length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 2, color: "#2e7d32" }}
                        >
                          Non-Conformities (Clause 8.9)
                        </Typography>
                        {getNCsByClause("8.9").map((nc, index) => (
                          <Box
                            key={nc.id}
                            sx={{
                              mb: 2,
                              p: 2,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Description:</strong> {nc.description}
                            </Typography>
                            {nc.document &&
                              nc.document.split("~").filter(Boolean).length >
                                0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      color: colors.grey[700],
                                      mb: 1,
                                      display: "block",
                                    }}
                                  >
                                    Documents:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                    }}
                                  >
                                    {nc.document
                                      .split("~")
                                      .filter(Boolean)
                                      .map((doc, i) => {
                                        const lower = doc.toLowerCase();
                                        return (
                                          <WorkingSpaceDocument
                                            key={i}
                                            doc={doc}
                                            index={i}
                                            isImage={lower.match(
                                              /\.(jpg|jpeg|png|gif|webp)$/,
                                            )}
                                            isPdf={lower.endsWith(".pdf")}
                                            isExcel={lower.match(
                                              /\.(xls|xlsx)$/,
                                            )}
                                            colors={colors}
                                            onImageClick={handleOpenLightbox}
                                          />
                                        );
                                      })}
                                  </Box>
                                </Box>
                              )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, mt: 2, fontWeight: 500 }}
                      >
                        Comments:
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#f8f9fa",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {formInputs.managementComments ||
                            "No information provided"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* PT Participation */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    PT Participation
                  </Typography>

                  {/* Availability of PT */}
                  <Grid container spacing={3} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        Availability of PT:
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Chip
                          label={formInputs.ptAvailability || "Not specified"}
                          color={
                            formInputs.ptAvailability === "Yes"
                              ? "success"
                              : formInputs.ptAvailability === "No"
                                ? "error"
                                : "default"
                          }
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Reason if PT is not available - Show only when No is selected */}
                  {formInputs.ptAvailability === "No" &&
                    formInputs.ptNotAvailableReason && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          backgroundColor: "#fff3e0",
                          borderRadius: 1,
                          border: "1px solid #ffcc02",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500, color: "#e65100" }}
                        >
                          Reason for PT not being available:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {formInputs.ptNotAvailableReason}
                        </Typography>
                      </Box>
                    )}

                  {/* PT Participation Assessment - Always Visible */}
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: "#fff8e1",
                      borderRadius: 2,
                      border: "1px solid #ffecb3",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#f57c00" }}
                      >
                        PT Participation Assessment
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={handleViewPTData}
                          sx={{
                            borderColor: "#f57c00",
                            color: "#f57c00",
                            "&:hover": {
                              borderColor: "#e65100",
                              backgroundColor: "#fff3e0",
                            },
                          }}
                        >
                          View PT Data
                        </Button>
                        {cabDetails?.QMSFile && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DescriptionIcon />}
                            onClick={handleViewQMSDocument}
                            sx={{
                              borderColor: "#1976d2",
                              color: "#1976d2",
                              "&:hover": {
                                borderColor: "#1565c0",
                                backgroundColor: "#e3f2fd",
                              },
                            }}
                          >
                            View PT Document
                          </Button>
                        )}
                      </Box>
                    </Box>
                    <Grid container spacing={3}>
                      {/* Coverage of scope */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Coverage of scope:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Chip
                            label={
                              formInputs.ptCoverageOfScope || "Not specified"
                            }
                            color={
                              formInputs.ptCoverageOfScope === "Adequate"
                                ? "success"
                                : formInputs.ptCoverageOfScope ===
                                    "Not Adequate"
                                  ? "error"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Grid>

                      {/* Outlier */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Outlier:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Chip
                            label={formInputs.ptOutlier || "Not specified"}
                            color={
                              formInputs.ptOutlier === "Yes"
                                ? "warning"
                                : formInputs.ptOutlier === "No"
                                  ? "success"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Grid>

                      {/* Root cause analysis remarks - Show only when Outlier is Yes */}
                      {formInputs.ptOutlier === "Yes" &&
                        formInputs.ptOutlierRemarks && (
                          <Grid item xs={12}>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, fontWeight: 500 }}
                            >
                              Remarks on root cause analysis:
                            </Typography>
                            <Box
                              sx={{
                                p: 2,
                                backgroundColor: "#ffffff",
                                borderRadius: 1,
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap" }}
                              >
                                {formInputs.ptOutlierRemarks}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* ILC Participation */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    ILC Participation
                  </Typography>

                  {/* Keep the original text area for general ILC participation details */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {formInputs.ilcParticipation ||
                        "No information provided about ILC participation"}
                    </Typography>
                  </Box>

                  {/* Assessment Questions */}
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: "#f3e5f5",
                      borderRadius: 2,
                      border: "1px solid #e1bee7",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#7b1fa2" }}
                      >
                        ILC Assessment Questions
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={handleViewILCData}
                        sx={{
                          borderColor: "#7b1fa2",
                          color: "#7b1fa2",
                          "&:hover": {
                            borderColor: "#6a1b9a",
                            backgroundColor: "rgba(123, 31, 162, 0.08)",
                          },
                        }}
                      >
                        View ILC Data
                      </Button>
                    </Box>
                    <Grid container spacing={3}>
                      {/* Coverage of scope through PT and/or ILC */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Coverage of scope through PT and / or ILC:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Chip
                            label={
                              formInputs.ilcCoverageOfScope || "Not specified"
                            }
                            color={
                              formInputs.ilcCoverageOfScope === "Adequate"
                                ? "success"
                                : formInputs.ilcCoverageOfScope ===
                                    "Not Adequate"
                                  ? "error"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Grid>

                      {/* Outlier */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Outlier:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Chip
                            label={formInputs.ilcOutlier || "Not specified"}
                            color={
                              formInputs.ilcOutlier === "Yes"
                                ? "warning"
                                : formInputs.ilcOutlier === "No"
                                  ? "success"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Grid>

                      {/* Root cause analysis remarks - Show only when Outlier is Yes */}
                      {formInputs.ilcOutlier === "Yes" &&
                        formInputs.ilcOutlierRemarks && (
                          <Grid item xs={12}>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, fontWeight: 500 }}
                            >
                              Remarks on root cause analysis of the lab:
                            </Typography>
                            <Box
                              sx={{
                                p: 2,
                                backgroundColor: "#ffffff",
                                borderRadius: 1,
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap" }}
                              >
                                {formInputs.ilcOutlierRemarks}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* Other Quality Assurance Measures */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Other Quality Assurance Measures
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {formInputs.otherQualityMeasures ||
                        "No other quality assurance measures provided"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Summary of Assessment */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Summary of Assessment (NAF 6)
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: "#f8f9fa",
                borderRadius: 2,
                mb: 2,
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.6, whiteSpace: "pre-wrap" }}
              >
                {assessmentReportData?.assessmentSummary ||
                  "Loading Please wait..."}
              </Typography>
            </Box>

            {/* Assessment Team Recommendations */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Assessment Team Recommendations (NAF 6)
            </Typography>
            <Box sx={{ p: 2, bgcolor: "#e8f5e8", borderRadius: 2 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {assessmentReportData?.recommendationOfAssessmentTeam ||
                  "Loading Please wait..."}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Observations/Comments by Concerned Team Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "purple", mr: 2, width: 40, height: 40 }}>
                <AssessmentIcon />
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Observations/ Comments by Concerned Team
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Advisor/Consultant/Internal Auditor */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Advisor / Consultant / Internal Auditor and their
                    Organization
                  </Typography>
                  <textarea
                    rows={4}
                    value={formInputs.advisorConsultant}
                    onChange={handleInputChange("advisorConsultant")}
                    disabled={hasExistingData}
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      resize: "vertical",
                      outline: "none",
                      backgroundColor: hasExistingData ? "#f0f0f0" : "#fafafa",
                      cursor: hasExistingData ? "not-allowed" : "text",
                      opacity: hasExistingData ? 0.7 : 1,
                      transition: "border-color 0.2s ease",
                    }}
                    placeholder={
                      hasExistingData
                        ? "Data already exists"
                        : "Enter details about advisor/consultant/internal auditor and their organization..."
                    }
                    onFocus={(e) =>
                      !hasExistingData &&
                      (e.target.style.borderColor = "#1976d2")
                    }
                    onBlur={(e) =>
                      !hasExistingData &&
                      (e.target.style.borderColor = "#e0e0e0")
                    }
                  />
                </Box>
              </Grid>

              {/* Adequacy of Organization */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Adequacy of the Organization and Procedures Adopted by the
                    CAB
                  </Typography>
                  <textarea
                    rows={4}
                    value={formInputs.adequacyOrganization}
                    onChange={handleInputChange("adequacyOrganization")}
                    disabled={hasExistingData}
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      resize: "vertical",
                      outline: "none",
                      backgroundColor: hasExistingData ? "#f5f5f5" : "#fafafa",
                      cursor: hasExistingData ? "not-allowed" : "text",
                      opacity: hasExistingData ? 0.9 : 1,
                      transition: "border-color 0.2s ease",
                    }}
                    placeholder={
                      hasExistingData
                        ? ""
                        : "Enter assessment of adequacy of organization and procedures to give confidence in competence..."
                    }
                    onFocus={(e) =>
                      !hasExistingData &&
                      (e.target.style.borderColor = "#1976d2")
                    }
                    onBlur={(e) =>
                      !hasExistingData &&
                      (e.target.style.borderColor = "#e0e0e0")
                    }
                  />
                </Box>
              </Grid>

              {/* Other Information */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Any Other Information That Can Assist in Decision Making
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "italic",
                        color: "text.secondary",
                        mt: 0.5,
                      }}
                    >
                      (Like misuse of NABL symbol, outcome of unannounced
                      audits, complaints)
                    </Typography>
                  </Typography>
                  <textarea
                    rows={4}
                    value={formInputs.otherInformation}
                    onChange={handleInputChange("otherInformation")}
                    disabled={hasExistingData}
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      resize: "vertical",
                      outline: "none",
                      backgroundColor: hasExistingData ? "#f5f5f5" : "#fafafa",
                      cursor: hasExistingData ? "not-allowed" : "text",
                      opacity: hasExistingData ? 0.9 : 1,
                      transition: "border-color 0.2s ease",
                    }}
                    placeholder={
                      hasExistingData
                        ? ""
                        : "Enter any other relevant information like misuse of NABL symbol, outcome of unannounced audits, complaints..."
                    }
                    onFocus={(e) =>
                      !hasExistingData &&
                      (e.target.style.borderColor = "#1976d2")
                    }
                    onBlur={(e) =>
                      !hasExistingData &&
                      (e.target.style.borderColor = "#e0e0e0")
                    }
                  />
                </Box>
              </Grid>

              {/* Observations/Comments & Recommendations */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Observation / Comment & Recommendation
                  </Typography>
                  <textarea
                    rows={6}
                    value={formInputs.observationRecommendation}
                    onChange={handleInputChange("observationRecommendation")}
                    disabled={hasExistingData}
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      resize: "vertical",
                      outline: "none",
                      backgroundColor: hasExistingData ? "#f5f5f5" : "#fafafa",
                      cursor: hasExistingData ? "not-allowed" : "text",
                      opacity: hasExistingData ? 0.9 : 1,
                      transition: "border-color 0.2s ease",
                    }}
                    placeholder={
                      hasExistingData
                        ? ""
                        : "Enter observations, comments, and recommendations from the concerned team..."
                    }
                    onFocus={(e) =>
                      !hasExistingData &&
                      (e.target.style.borderColor = "#1976d2")
                    }
                    onBlur={(e) =>
                      !hasExistingData &&
                      (e.target.style.borderColor = "#e0e0e0")
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Additional Documents */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{ bgcolor: "secondary.main", mr: 2, width: 40, height: 40 }}
              >
                <DescriptionIcon />
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Additional Documents
                <Chip
                  label={additionalDocuments.length}
                  size="small"
                  sx={{
                    ml: 2,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              </Typography>
            </Box>

            {loadingDocuments ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : additionalDocuments.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <DescriptionIcon
                  sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                />
                <Typography variant="h6" color="textSecondary">
                  No additional documents found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  No additional documents have been uploaded for this assessment
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{ borderRadius: 2, overflow: "hidden" }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        "& .MuiTableCell-head": {
                          color: "white",
                          fontWeight: 700,
                          fontSize: "1rem",
                          borderBottom: "none",
                        },
                      }}
                    >
                      <TableCell>S No.</TableCell>
                      <TableCell>Document Name</TableCell>
                      <TableCell>Target Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Upload Date</TableCell>
                      <TableCell>Uploaded By</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {additionalDocuments.map((document, index) => (
                      <TableRow
                        key={document.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#fafbfc" : "white",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "rgba(102, 126, 234, 0.08)",
                            transform: "scale(1.01)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: "#2c3e50" }}
                          >
                            {document.documentName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getTargetTypeDisplay(document.targetType)}
                            color={getTargetTypeColor(document.targetType)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {document.description || "No description"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {document.uploadDate
                              ? formatDate(document.uploadDate)
                              : "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {document.uploadedBy || "Unknown"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDocument(document)}
                            sx={{
                              borderColor: "primary.main",
                              color: "primary.main",
                              "&:hover": {
                                borderColor: "primary.dark",
                                backgroundColor: "primary.50",
                              },
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Comments Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Comments For Putting in AC Process
            </Typography>
            <Box sx={{ mb: 2 }}>
              <textarea
                rows={8}
                value={formInputs.acProcessComments}
                onChange={handleInputChange("acProcessComments")}
                disabled={hasExistingData}
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  resize: "vertical",
                  outline: "none",
                  backgroundColor: hasExistingData ? "#f5f5f5" : "#f8f9fa",
                  cursor: hasExistingData ? "not-allowed" : "text",
                  opacity: hasExistingData ? 0.9 : 1,
                  lineHeight: "1.6",
                  transition: "border-color 0.2s ease",
                }}
                placeholder={
                  hasExistingData
                    ? ""
                    : "Enter comments for putting in AC process..."
                }
                onFocus={(e) =>
                  !hasExistingData && (e.target.style.borderColor = "#1976d2")
                }
                onBlur={(e) =>
                  !hasExistingData && (e.target.style.borderColor = "#e0e0e0")
                }
              />
            </Box>
          </CardContent>
        </Card>

        {/* Decision Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Decision to be process
            </Typography>
            <RadioGroup
              value={decisionProcess}
              onChange={(event) => setDecisionProcess(event.target.value)}
              disabled={hasExistingData}
              sx={{ display: "flex", flexDirection: "row", gap: 3 }}
            >
              <FormControlLabel
                value="internal_approval"
                control={<Radio color="success" disabled={hasExistingData} />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Through Internal Approval
                    </Typography>
                  </Box>
                }
                disabled={hasExistingData}
                sx={{
                  margin: 0,
                  padding: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  backgroundColor:
                    decisionProcess === "internal_approval"
                      ? "success.50"
                      : "transparent",
                  transition: "all 0.2s ease",
                  "&:hover": !hasExistingData
                    ? {
                        borderColor: "success.main",
                        backgroundColor: "success.25",
                      }
                    : {},
                  ...(decisionProcess === "internal_approval" && {
                    borderColor: "success.main",
                    backgroundColor: "success.50",
                  }),
                  ...(hasExistingData && {
                    cursor: "not-allowed",
                    opacity: 0.7,
                  }),
                }}
              />
              <FormControlLabel
                value="acm"
                control={<Radio color="primary" disabled={hasExistingData} />}
                label={
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Through ACM
                  </Typography>
                }
                disabled={hasExistingData}
                sx={{
                  margin: 0,
                  padding: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  backgroundColor:
                    decisionProcess === "acm" ? "primary.50" : "transparent",
                  transition: "all 0.2s ease",
                  "&:hover": !hasExistingData
                    ? {
                        borderColor: "primary.main",
                        backgroundColor: "primary.25",
                      }
                    : {},
                  ...(decisionProcess === "acm" && {
                    borderColor: "primary.main",
                    backgroundColor: "primary.50",
                  }),
                  ...(hasExistingData && {
                    cursor: "not-allowed",
                    opacity: 0.7,
                  }),
                }}
              />
            </RadioGroup>
            {showAfterApprovalBox && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Enter Details After Approval"
                  value={afterApprovalRemark}
                  disabled
                  onChange={(e) => setAfterApprovalRemark(e.target.value)}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Submit Section */}
        {applicationStage === "6a" && (
          <Card
            sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
          >
            <CardContent sx={{ p: 4 }}>
              {/* Success/Error Messages */}
              {submitSuccess && (
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    "& .MuiAlert-message": {
                      fontWeight: 600,
                    },
                  }}
                >
                  ✅ Form11 Assessment Summary has been saved successfully!
                </Alert>
              )}

              {/* JD Action Messages */}
              {actionSuccess && (
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    "& .MuiAlert-message": {
                      fontWeight: 600,
                    },
                  }}
                >
                  {actionSuccess}
                </Alert>
              )}

              {(submitError || actionError) && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    "& .MuiAlert-message": {
                      fontWeight: 600,
                    },
                  }}
                  onClose={() => {
                    setSubmitError(null);
                    setActionError(null);
                  }}
                >
                  ❌ Error: {submitError || actionError}
                </Alert>
              )}

              {/* JD Approval/Rejection Section */}
              {hasExistingData && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
                  >
                    Joint Director Decision
                  </Typography>

                  {/* Check if already processed */}
                  {existingForm11Data?.JDirectorAccepted === null ||
                  existingForm11Data?.JDirectorAccepted === 2 ||
                  existingForm11Data?.JDirectorAccepted === undefined ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", gap: 3 }}
                    >
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={
                          isApproving ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <ThumbUpIcon />
                          )
                        }
                        onClick={handleApprove}
                        disabled={isApproving || isRejecting}
                        sx={{
                          px: 4,
                          py: 1.5,
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          background:
                            "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
                          boxShadow: "0 4px 15px rgba(76, 175, 80, 0.4)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #45a049 0%, #388e3c 100%)",
                            boxShadow: "0 6px 20px rgba(76, 175, 80, 0.6)",
                            transform: "translateY(-2px)",
                          },
                          "&:disabled": {
                            background: "#e0e0e0",
                            color: "#9e9e9e",
                            boxShadow: "none",
                            transform: "none",
                          },
                        }}
                      >
                        {isApproving ? "Approving..." : "Approve"}
                      </Button>

                      <Button
                        variant="contained"
                        size="large"
                        startIcon={
                          isRejecting ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <ThumbDownIcon />
                          )
                        }
                        onClick={handleReject}
                        disabled={isApproving || isRejecting}
                        sx={{
                          px: 4,
                          py: 1.5,
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          background:
                            "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
                          boxShadow: "0 4px 15px rgba(244, 67, 54, 0.4)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
                            boxShadow: "0 6px 20px rgba(244, 67, 54, 0.6)",
                            transform: "translateY(-2px)",
                          },
                          "&:disabled": {
                            background: "#e0e0e0",
                            color: "#9e9e9e",
                            boxShadow: "none",
                            transform: "none",
                          },
                        }}
                      >
                        {isRejecting ? "Rejecting..." : "Reject"}
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 3,
                        bgcolor: "grey.50",
                        borderRadius: 2,
                      }}
                    >
                      {existingForm11Data?.JDirectorAccepted === "1" && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, color: "success.main" }}
                          >
                            ✅ Application Approved
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Approved on:{" "}
                            {existingForm11Data?.JDirectorAcceptDate
                              ? new Date(
                                  existingForm11Data.JDirectorAcceptDate,
                                ).toLocaleString()
                              : "N/A"}
                          </Typography>
                        </Alert>
                      )}

                      {existingForm11Data?.JDirectorAccepted === "2" && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, color: "error.main" }}
                          >
                            ❌ Application Rejected
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Rejected on:{" "}
                            {existingForm11Data?.JDirectorAcceptDate
                              ? new Date(
                                  existingForm11Data.JDirectorAcceptDate,
                                ).toLocaleString()
                              : "N/A"}
                          </Typography>
                          {existingForm11Data?.JDirectorDenyReason && (
                            <Typography
                              variant="body2"
                              sx={{ mt: 1, fontWeight: 500 }}
                            >
                              Reason: {existingForm11Data.JDirectorDenyReason}
                            </Typography>
                          )}
                        </Alert>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Data Already Exists Message */}
              {hasExistingData && !existingForm11Data?.JDirectorAccepted && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 2,
                    "& .MuiAlert-message": {
                      fontWeight: 600,
                    },
                  }}
                >
                  ℹ️ Form11 is Prepared and ready for your decision.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Approval Confirmation Dialog */}
        <Dialog
          open={approvalDialogOpen}
          onClose={handleApproveCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, color: "success.main" }}>
            Approve Application
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to approve this Assessment Summary?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Once approved, this action cannot be undone and the application is
              send to Director for Approval.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleApproveCancel}
              variant="outlined"
              disabled={isApproving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveConfirm}
              variant="contained"
              color="success"
              disabled={isApproving}
              startIcon={
                isApproving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CheckIcon />
                )
              }
            >
              {isApproving ? "Approving..." : "Confirm Approval"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rejection Dialog */}
        <Dialog
          open={rejectionDialogOpen}
          onClose={handleRejectCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, color: "error.main" }}>
            Reject Application
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please provide a reason for rejecting this application:
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "error.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "error.main",
                  },
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleRejectCancel}
              variant="outlined"
              disabled={isRejecting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRejectConfirm}
              variant="contained"
              color="error"
              disabled={isRejecting || !rejectionReason.trim()}
              startIcon={
                isRejecting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CloseIcon />
                )
              }
            >
              {isRejecting ? "Rejecting..." : "Confirm Rejection"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Lightbox Dialog for Image Preview */}
        <Dialog
          open={lightboxOpen}
          onClose={handleCloseLightbox}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              boxShadow: "none",
            },
          }}
        >
          <DialogContent
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 0,
              position: "relative",
            }}
          >
            <IconButton
              onClick={handleCloseLightbox}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                color: "white",
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={lightboxImage}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: "90vh" }}
            />
          </DialogContent>
        </Dialog>

        {/* PT Data Popup Dialog */}
        <Dialog
          open={ptDataPopupOpen}
          onClose={handleClosePTDataPopup}
          maxWidth="lg"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: 2,
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#f57c00",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssessmentIcon />
              <Typography variant="h6" component="span">
                PT Participation Data
              </Typography>
            </Box>
            <IconButton
              onClick={handleClosePTDataPopup}
              sx={{
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              ✕
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {ptDataLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 4,
                }}
              >
                <CircularProgress sx={{ color: "#f57c00" }} />
                <Typography sx={{ ml: 2, color: "text.secondary" }}>
                  Loading PT data...
                </Typography>
              </Box>
            ) : ptDataError ? (
              <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {ptDataError}
                </Alert>
                <Button
                  variant="outlined"
                  onClick={loadPTData}
                  startIcon={<VisibilityIcon />}
                >
                  Retry
                </Button>
              </Box>
            ) : ptData && ptData.length > 0 ? (
              <TableContainer sx={{ maxHeight: "60vh" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          bgcolor: "#f57c00",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        Participation In
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#f57c00",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        Provider Name
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#f57c00",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        ILC Details
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#f57c00",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        Remarks
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#f57c00",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        Created On
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ptData.map((row, index) => (
                      <TableRow
                        key={row.Id || index}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "rgba(245, 124, 0, 0.05)",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(245, 124, 0, 0.1)",
                          },
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={row.participationIn || "N/A"}
                            size="small"
                            sx={{
                              bgcolor: "#fff3e0",
                              color: "#f57c00",
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {getProviderName(
                              row.participationIn,
                              row.nodalLaboratory,
                              row.nodalLaboratoryOthers,
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 250,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={row.ilcDetails || "N/A"}
                          >
                            {row.ilcDetails || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 250,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={row.remarks || "N/A"}
                          >
                            {row.remarks || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            {row.CreatedOn
                              ? new Date(row.CreatedOn).toLocaleDateString(
                                  "en-IN",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "2-digit",
                                  },
                                )
                              : "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <InfoIcon
                  sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography
                  variant="h6"
                  sx={{ color: "text.secondary", mb: 1 }}
                >
                  No PT Data Found
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No proficiency testing participation data has been saved for
                  this application yet.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "#fafafa" }}>
            <Button
              onClick={handleClosePTDataPopup}
              variant="outlined"
              sx={{
                borderColor: "#f57c00",
                color: "#f57c00",
                "&:hover": {
                  borderColor: "#e65100",
                  bgcolor: "#fff3e0",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ILC Data Popup Dialog */}
        <Dialog
          open={ilcDataPopupOpen}
          onClose={handleCloseILCDataPopup}
          maxWidth="lg"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: 2,
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#7b1fa2",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssessmentIcon />
              <Typography variant="h6" component="span">
                ILC Participation Data
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseILCDataPopup}
              sx={{
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              ✕
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {ilcDataLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 4,
                }}
              >
                <CircularProgress sx={{ color: "#7b1fa2" }} />
                <Typography sx={{ ml: 2, color: "text.secondary" }}>
                  Loading ILC data...
                </Typography>
              </Box>
            ) : ilcDataError ? (
              <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {ilcDataError}
                </Alert>
                <Button
                  variant="outlined"
                  onClick={loadILCData}
                  startIcon={<VisibilityIcon />}
                >
                  Retry
                </Button>
              </Box>
            ) : ilcData && ilcData.length > 0 ? (
              <TableContainer sx={{ maxHeight: "60vh" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          bgcolor: "#7b1fa2",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        Participation In
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#7b1fa2",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        Provider Name
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#7b1fa2",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        ILC Details
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#7b1fa2",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        Remarks
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#7b1fa2",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        Created On
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ilcData.map((row, index) => (
                      <TableRow
                        key={row.Id || index}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "rgba(123, 31, 162, 0.05)",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(123, 31, 162, 0.1)",
                          },
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={row.participationIn || "N/A"}
                            size="small"
                            sx={{
                              bgcolor: "#f3e5f5",
                              color: "#7b1fa2",
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {getProviderName(
                              row.participationIn,
                              row.nodalLaboratory,
                              row.nodalLaboratoryOthers,
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 250,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={row.ilcDetails || "N/A"}
                          >
                            {row.ilcDetails || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 250,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={row.remarks || "N/A"}
                          >
                            {row.remarks || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            {row.CreatedOn
                              ? new Date(row.CreatedOn).toLocaleDateString(
                                  "en-IN",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "2-digit",
                                  },
                                )
                              : "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <InfoIcon
                  sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography
                  variant="h6"
                  sx={{ color: "text.secondary", mb: 1 }}
                >
                  No ILC Data Found
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No ILC participation data has been saved for this application
                  yet.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "#fafafa" }}>
            <Button
              onClick={handleCloseILCDataPopup}
              variant="outlined"
              sx={{
                borderColor: "#7b1fa2",
                color: "#7b1fa2",
                "&:hover": {
                  borderColor: "#6a1b9a",
                  bgcolor: "#f3e5f5",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Form11;
